import { LineChartOutlined, TableOutlined } from '@ant-design/icons';
import { Button, Card, Table, Tooltip as AntTooltip, Typography } from 'antd';
import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  LabelList,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { isolatedDot } from '../Dashboard/KpiChart';
import {
  IReportBucket,
  MAIN_METRICS,
  MainMetricId,
  Unit,
  formatValue,
  toIndex,
} from './companyReport';

const { Text } = Typography;

/**
 * 系列色。dataviz の参照パレットの先頭4色（青・橙・水色・黄）で、隣り合う色は色覚多様性の判別基準を満たす
 * （validate_palette.js で確認済み）。水色・黄は白地で 3:1 を下回るので、線の右端に実数を直接出し、表表示も付ける。
 */
const COLORS: Record<MainMetricId, string> = {
  drivingLowScore: '#2a78d6',
  cogevoPerUser: '#eb6834',
  stepsPerDay: '#1baf7a',
  familyActivity: '#eda100',
};
const INK_MUTED = '#666666';
const GRID = '#E5E5E5';

interface IProps {
  buckets: IReportBucket[];
  unit: Unit;
}

const periodText = (b: IReportBucket) =>
  `${b.from} 〜 ${b.to}${b.partial ? '（途中経過）' : ''}`;

export const IndexChart: React.FC<IProps> = ({ buckets, unit }) => {
  const [asTable, setAsTable] = useState(false);
  const [hidden, setHidden] = useState<Set<MainMetricId>>(new Set());

  const indexed = useMemo(
    () =>
      Object.fromEntries(
        MAIN_METRICS.map((m) => [m.id, toIndex(buckets, m.id)]),
      ) as Record<MainMetricId, ReturnType<typeof toIndex>>,
    [buckets],
  );
  const data = buckets.map((b, i) => ({
    ...b,
    ...Object.fromEntries(
      MAIN_METRICS.map((m) => [m.id, indexed[m.id].points[i]]),
    ),
  }));
  // isolatedDot は buckets[i].values[id] を見るので、指数を values に入れた形で渡す
  const dotSource = data.map((d) => ({ values: d })) as unknown as Parameters<
    typeof isolatedDot
  >[0];
  const visible = MAIN_METRICS.filter((m) => !hidden.has(m.id));
  const baseLabel = (id: MainMetricId) => {
    const key = indexed[id].baseKey;

    return key ? (buckets.find((b) => b.key === key)?.label ?? key) : null;
  };

  const toggle = (id: MainMetricId) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);

      return next;
    });

  const ChartTooltip: React.FC<TooltipProps<number, string>> = ({
    active,
    payload,
  }) => {
    if (!active || !payload?.length) return null;
    const bucket = payload[0].payload as IReportBucket &
      Record<MainMetricId, number | null>;

    return (
      <div className='bg-white border border-line-subtle rounded-md px-md py-sm shadow-sm text-[12px]'>
        <div className='text-fg-muted mb-xs'>{periodText(bucket)}</div>
        {visible.map((m) => (
          <div key={m.id} className='flex items-center gap-sm'>
            <span
              className='inline-block w-[8px] h-[8px] rounded-full'
              style={{ background: COLORS[m.id] }}
            />
            <span className='text-fg-muted'>{m.label}</span>
            <span className='ml-auto pl-md text-fg font-semibold'>
              {formatValue(bucket.values[m.id], m.digits, m.unit)}
            </span>
            <span className='text-fg-muted w-[56px] text-right'>
              {typeof bucket[m.id] === 'number' ? `(${bucket[m.id]})` : ''}
            </span>
          </div>
        ))}
      </div>
    );
  };

  /** 線の右端（最後に値がある点）にだけ実数を出す */
  const endLabel =
    (id: MainMetricId, digits: number, unitText: string) =>
    (props: { x?: number; y?: number; index?: number }) => {
      const points = indexed[id].points;
      let last = -1;
      points.forEach((p, i) => {
        if (typeof p === 'number') last = i;
      });
      if (props.index !== last || props.x == null || props.y == null)
        return null;

      return (
        <text x={props.x + 6} y={props.y} dy={4} fontSize={11} fill='#0b0b0b'>
          {formatValue(buckets[last].values[id], digits, unitText)}
        </text>
      );
    };

  return (
    <Card
      size='small'
      title={
        <div className='py-xs'>
          <div className='text-[14px]'>
            主要指標の推移（最初の{unit === 'week' ? '週' : '月'} = 100）
          </div>
          <Text
            type='secondary'
            className='text-[12px] font-normal whitespace-normal'
          >
            単位の違う指標を重ねるため、各指標の最初に値があった期間を 100
            にしています。線の右端とツールチップは実際の値（括弧内は指数）。
            凡例をクリックすると表示を切り替えられます。
          </Text>
        </div>
      }
      extra={
        <AntTooltip title={asTable ? 'グラフで見る' : '表で見る'}>
          <Button
            type='text'
            size='small'
            icon={asTable ? <LineChartOutlined /> : <TableOutlined />}
            onClick={() => setAsTable((v) => !v)}
            aria-label={asTable ? 'グラフで見る' : '表で見る'}
          />
        </AntTooltip>
      }
    >
      {asTable ? (
        <Table
          size='small'
          rowKey='key'
          pagination={false}
          scroll={{ y: 360, x: 'max-content' }}
          dataSource={[...buckets].reverse()}
          columns={[
            {
              title: '期間',
              key: 'period',
              render: (_: unknown, b: IReportBucket) => periodText(b),
            },
            ...MAIN_METRICS.map((m) => ({
              title: m.label,
              key: m.id,
              align: 'right' as const,
              render: (_: unknown, b: IReportBucket) => {
                const i = buckets.indexOf(b);
                const idx = indexed[m.id].points[i];

                return (
                  <span>
                    {formatValue(b.values[m.id], m.digits, m.unit)}
                    {typeof idx === 'number' && (
                      <Text type='secondary' className='ml-xs text-[11px]'>
                        ({idx})
                      </Text>
                    )}
                  </span>
                );
              },
            })),
          ]}
        />
      ) : (
        <div style={{ height: 360 }}>
          <ResponsiveContainer width='100%' height='100%'>
            <LineChart
              data={data}
              margin={{ top: 8, right: 72, bottom: 0, left: 0 }}
            >
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis
                dataKey='label'
                tick={{ fill: INK_MUTED, fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: GRID }}
                minTickGap={16}
              />
              <YAxis
                tick={{ fill: INK_MUTED, fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={48}
              />
              <ReferenceLine y={100} stroke={INK_MUTED} strokeDasharray='3 3' />
              <Tooltip
                content={<ChartTooltip />}
                cursor={{ stroke: INK_MUTED, strokeDasharray: '3 3' }}
              />
              <Legend
                verticalAlign='top'
                align='right'
                iconType='circle'
                iconSize={8}
                height={28}
                onClick={(e) =>
                  toggle((e as unknown as { dataKey: MainMetricId }).dataKey)
                }
                formatter={(value: string, entry) => {
                  const id = (entry as unknown as { dataKey: MainMetricId })
                    .dataKey;
                  const base = baseLabel(id);

                  return (
                    <span
                      className={`text-[12px] cursor-pointer ${hidden.has(id) ? 'text-fg-muted line-through' : 'text-fg'}`}
                    >
                      {value}
                      {base
                        ? `（基準 ${base}）`
                        : indexed[id].allZero
                          ? '（期間中 0）'
                          : '（データなし）'}
                    </span>
                  );
                }}
              />
              {MAIN_METRICS.map((m) => (
                <Line
                  key={m.id}
                  type='monotone'
                  dataKey={m.id}
                  name={m.label}
                  hide={hidden.has(m.id)}
                  stroke={COLORS[m.id]}
                  strokeWidth={2}
                  dot={isolatedDot(dotSource, m.id, COLORS[m.id])}
                  activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2 }}
                  connectNulls={false}
                  isAnimationActive={false}
                >
                  <LabelList
                    dataKey={m.id}
                    content={endLabel(m.id, m.digits, m.unit) as never}
                  />
                </Line>
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className='mt-sm flex flex-col gap-2xs'>
        {MAIN_METRICS.map((m) => (
          <Text key={m.id} type='secondary' className='text-[12px]'>
            <span
              className='inline-block w-[8px] h-[8px] rounded-full mr-xs'
              style={{ background: COLORS[m.id] }}
            />
            {m.label}: {m.note}
            {unit === 'month' &&
            (m.id === 'drivingLowScore' || m.id === 'familyActivity')
              ? '（月は週の平均）'
              : ''}
          </Text>
        ))}
      </div>
    </Card>
  );
};
